import type { CSSProperties } from "react";
import { Icons } from "../components/Icons.js";
import { RecordButton } from "../components/RecordButton.js";
import { fmtClock } from "../lib/format.js";
import type { Recording } from "../core/useRecording.js";

/**
 * Zone de capture (cahier des charges §7.2) : bouton principal au centre, bascule de mode à droite,
 * petits boutons Annuler / Valider dessous. Le fond change selon le mode. En **interface simple**
 * (§7.4) : uniquement la note cumulée, pas de bascule ni de petits boutons (finalisation auto).
 * Au-dessus du bouton, le bandeau d'alerte quota (80 % orange / 100 % rouge, §10.4).
 */
export function CaptureView({
  recording,
  btnScale = 1,
  desktop = false,
  simple = false,
  usageRatio = null,
  bgDaily = "",
  bgStandalone = "",
}: {
  recording: Recording;
  btnScale?: number;
  desktop?: boolean;
  simple?: boolean;
  usageRatio?: number | null;
  bgDaily?: string;
  bgStandalone?: string;
}) {
  const rec = recording;
  const atRest = rec.phase === "idle";
  const isRecording = rec.phase === "recording";
  const modeLabel = rec.mode === "daily" ? "Notes cumulées" : "Note indépendante";

  const modeBg = rec.mode === "daily" ? bgDaily : bgStandalone;
  const bgStyle: CSSProperties | undefined = modeBg
    ? modeBg.startsWith("url")
      ? { backgroundImage: modeBg, backgroundSize: "cover", backgroundPosition: "center" }
      : { background: modeBg }
    : undefined;

  const status = (() => {
    if (rec.phase === "finalizing") return <div className="rec-hint">Finalisation…</div>;
    if (isRecording) {
      return (
        <div className="rec-status">
          <span className="rec-live">
            <span className="rec-livedot" />
            {fmtClock(rec.elapsed)}
          </span>
          <div className="cap-seg">Segment {rec.segmentCount + 1}</div>
        </div>
      );
    }
    if (rec.phase === "paused") {
      return (
        <div className="rec-status">
          <div className="rec-hint">En pause · {rec.segmentCount} segment{rec.segmentCount > 1 ? "s" : ""}</div>
          <div className="rec-subhint">
            {rec.mode === "daily" ? "Reprenez, ou laissez la finalisation automatique" : "Reprenez, ou validez pour terminer"}
          </div>
        </div>
      );
    }
    return (
      <div className="rec-status">
        <div className="rec-hint">Appuyez pour enregistrer</div>
        <div className="rec-subhint">maintenez puis relâchez, ou appuyez à nouveau pour mettre en pause</div>
      </div>
    );
  })();

  return (
    <div className={"capture mode-" + rec.mode} style={bgStyle}>
      {usageRatio !== null && usageRatio >= 0.8 && (
        <div className={"quota-alert " + (usageRatio >= 1 ? "red" : "orange")}>
          {usageRatio >= 1 ? "Quota atteint (minutes ou volume audio)" : "Quota bientôt atteint"}
        </div>
      )}

      {!simple && (
        <div className="cap-modebadge">
          <span className={"cap-modedot mode-" + rec.mode} />
          Mode {modeLabel}
        </div>
      )}

      {/* Titre de la note indépendante : au-dessus du bouton d'enregistrement, saisissable au repos. */}
      {!simple && atRest && rec.mode === "standalone" && (
        <input
          className="cap-title"
          type="text"
          placeholder="Titre de la note (optionnel)"
          value={rec.standaloneTitle}
          onChange={(e) => rec.setStandaloneTitle(e.target.value)}
        />
      )}

      <div className="cap-main">
        <RecordButton
          recording={isRecording}
          level={rec.level}
          scale={btnScale * (desktop ? 1.34 : 1.18)}
          onPointerDown={rec.pressMainDown}
          onPointerUp={rec.pressMainUp}
        />
        {!simple && (
          <button
            className="modetoggle"
            disabled={!atRest}
            onClick={() => rec.setMode(rec.mode === "daily" ? "standalone" : "daily")}
            aria-label="Changer de mode"
            title={atRest ? "Changer de mode (au repos uniquement)" : "Bascule possible seulement au repos"}
          >
            <Icons.Refresh size={20} />
            {rec.mode === "daily" ? (
              <span className="modetoggle-l">
                Note
                <br />
                indépendante
              </span>
            ) : (
              <span className="modetoggle-l">
                Notes
                <br />
                cumulées
              </span>
            )}
          </button>
        )}
      </div>

      {status}

      {/* En interface simple, ces contrôles restent masqués au repos (finalisation auto), mais
          réapparaissent dès qu'une capture est active : sinon une note indépendante encore en cours au
          moment du passage en simple n'aurait aucune issue (ni validation, ni annulation). */}
      {(!simple || rec.active) && (
        <div className="cap-controls">
          <button
            className="cap-small cap-cancel"
            disabled={!rec.canCancel}
            onPointerDown={rec.pressCancelDown}
            onPointerUp={rec.pressCancelUp}
            title="Appui bref : supprimer le dernier segment · appui long : tout annuler"
          >
            <Icons.Trash size={18} /> Annuler
          </button>
          <button className="cap-small cap-validate" disabled={!rec.active || rec.busy} onClick={rec.validate} title="Finaliser l'enregistrement">
            <Icons.Check size={18} /> Valider
          </button>
        </div>
      )}

      {rec.notice && <div className="cap-notice">{rec.notice}</div>}
      {rec.error && <div className="cap-error">{rec.error}</div>}
    </div>
  );
}
